import { Popover } from '@mui/material';
import { isEqual, sortBy } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import CrossIcon from '../../../assets/svg/CrossIcon';
import GlassIcon from '../../../assets/svg/GlassIcon';
import { Button, IconButton } from '../../Shared';
import { getFilteredTeam, getFilteredTeams, isTenantFound } from './helper';
import { NoResultsFound } from './NoResultsFound';
import { Tenant } from './Tenant';

export const TenantAndTeamFilter = ({
  callerIds,
  onChangeCallerIds,
  onChangeTeams,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [selectedCallerIds, setSelectedCallerIds] = useState(callerIds);
  const [teams, setTeams] = useState([]);
  const [isTeamsLoading, setIsTeamsLoading] = useState(true);
  const [isTeamsError, setIsTeamsError] = useState(false);

  const fetchTeams = useCallback(async () => {
    setIsTeamsLoading(true);
    setIsTeamsError(false);

    try {
      const response = await fetch('/admin/get-admin-teams');
      if (response.status !== 200) throw new Error();
      const data = await response.json();
      setIsTeamsLoading(false);
      setTeams(data.team);
      onChangeTeams?.(data.team);
    } catch {
      setIsTeamsLoading(false);
      setIsTeamsError(true);
      toast.error('Failed to load teams!', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  }, []);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  const onChangeSearchValue = e => {
    const nextSearchValue = e.target.value;
    setSearchValue(nextSearchValue);
  };

  const resetSearchValue = () => {
    setSearchValue('');
  };

  const onOpenPopover = e => {
    if (e.target.localName !== 'button') return;
    setSearchValue('');
    setSelectedCallerIds(callerIds);
    setAnchorEl(e.currentTarget);
  };

  const onClosePopover = () => {
    setAnchorEl(null);
  };

  const handleTenant = (tenantID, tenantTeam) => {
    setSelectedCallerIds(prev => {
      const selectedFromThisTenant = prev.filter(
        ({ TenantID }) => TenantID === tenantID,
      );

      const selectedFromOtherTenant = prev.filter(
        ({ TenantID }) => TenantID !== tenantID,
      );

      if (tenantTeam.length === selectedFromThisTenant.length) {
        return selectedFromOtherTenant;
      }

      return [...selectedFromOtherTenant, ...tenantTeam];
    });
  };

  const handleCaller = caller => {
    setSelectedCallerIds(prev => {
      const isSelected = !!prev.find(
        ({ TenantID, CallerID }) =>
          TenantID === caller.TenantID && CallerID === caller.CallerID,
      );

      if (isSelected)
        return prev.filter(
          ({ TenantID, CallerID }) =>
            !(TenantID === caller.TenantID && CallerID === caller.CallerID),
        );

      return [...prev, caller];
    });
  };

  const onChange = () => {
    onChangeCallerIds(prev => {
      return {
        ...prev,
        callerIds: selectedCallerIds,
      };
    });
    onClosePopover();
  };

  const onSelectAll = () => {
    setSelectedCallerIds(prev => {
      const nextCallerIds = teams.flatMap(({ TenantTeam }) => TenantTeam);

      return nextCallerIds.length === prev.length ? [] : nextCallerIds;
    });
  };

  const reset = () => {
    onChangeCallerIds(prev => {
      return {
        ...prev,
        callerIds: [],
      };
    });
  };

  const open = Boolean(anchorEl);
  const hasSearchValue = !!searchValue;
  const filteredTeams = getFilteredTeams(teams, searchValue);
  const hasTeams = filteredTeams.length > 0;
  const hasSelectedCallerIds = callerIds.length > 0;
  const isButtonApplyDisabled = isEqual(
    sortBy(selectedCallerIds, 'TenantID'),
    sortBy(callerIds, 'TenantID'),
  );

  if (isTeamsError) return null;

  return (
    <>
      <button
        className={twMerge(
          'flex items-center gap-2 rounded-full py-[11px] px-6 text-brand-900 border border-brand-900 font-medium text-[16px]/[24px] focus-visible:outline-none',
          hasSelectedCallerIds && 'bg-brand-900 text-white !pl-6 !pr-5',
        )}
        onClick={onOpenPopover}
        disabled={isTeamsLoading}
      >
        {hasSelectedCallerIds
          ? `Client & Team +${callerIds.length}`
          : 'Client & Team'}
        {hasSelectedCallerIds && (
          <div onClick={reset}>
            <CrossIcon className="w-4 h-4 text-white" />
          </div>
        )}
      </button>
      <Popover
        open={open}
        slotProps={{
          paper: {
            className:
              '!rounded-xl mt-2 !shadow-[0_4px_20px_0_rgba(17,17,58,0.08)] bg-white !max-w-[280px] !w-full max-h-[80vh] overflow-hidden flex flex-col',
          },
        }}
        anchorEl={anchorEl}
        onClose={onClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <div className="overflow-hidden flex flex-col max-h-[672px]">
          <div className="p-1">
            <div className="relative">
              <GlassIcon className="w-5 h-5 text-brand-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                value={searchValue}
                onChange={onChangeSearchValue}
                placeholder="Search"
                className="w-full py-2 pl-11 pr-[38px] text-[16px]/[24px] text-base-1000 border-none placeholder:text-base-800 focus:outline-none"
              />
              {hasSearchValue && (
                <IconButton
                  buttonProps={{
                    className:
                      'w-6 h-6 !absolute right-3 top-1/2 -translate-y-1/2',
                    onClick: resetSearchValue,
                  }}
                  iconProps={{
                    name: 'cross',
                    className: 'h-4',
                  }}
                />
              )}
            </div>
          </div>
          {hasTeams ? (
            <div className="flex-1 overflow-auto max-h-[552px]">
              <div className="flex flex-col gap-1">
                {teams.map(({ TenantID, TenantName, TenantTeam }) => {
                  const filteredTeam = getFilteredTeam(TenantTeam, searchValue);
                  const hasTeam = filteredTeam.length > 0;
                  const isTenantVisible =
                    isTenantFound(searchValue, TenantName) || hasTeam;

                  return (
                    isTenantVisible && (
                      <Tenant
                        key={TenantID}
                        tenantID={TenantID}
                        tenantName={TenantName}
                        tenantTeam={TenantTeam}
                        filteredTeam={filteredTeam}
                        selectedCallerIds={selectedCallerIds}
                        handleTenant={() => {
                          handleTenant(TenantID, TenantTeam);
                        }}
                        handleCaller={handleCaller}
                      />
                    )
                  );
                })}
              </div>
            </div>
          ) : (
            <NoResultsFound />
          )}
          <div className="flex gap-3 justify-end px-4 py-3 border-t border-t-base-200">
            <Button
              title="Select All"
              variant="secondary"
              onClick={onSelectAll}
              disabled={hasSearchValue}
            />
            <Button
              title="Apply"
              onClick={onChange}
              disabled={isButtonApplyDisabled}
            />
          </div>
        </div>
      </Popover>
    </>
  );
};
